import { useState } from "react";
import { toast } from "sonner";
import { subDays, format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Check, Menu, RefreshCw } from "lucide-react";
import { LeadboxLayout } from "../components/LeadboxLayout";
import { LeadTableNew } from "../leads/components/LeadTableNew";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/DatePicker";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { BaseLead } from "@/types/lead";
import { useLead } from "@/hooks/useLead";
import { useNavigate } from "react-router-dom";
import { getLanguage } from "@/utils/languageUtils";
import {
  commonButtonLabels,
  commonMiscLabels,
  commonTitleLabels,
  leadUILabels,
} from "@/i18n";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";

const MCLeads = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Use the lead hook to get leads data
  const { useGetAllLeads } = useLead();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "pageSize:mcLeads",
    10,
    [10, 25, 50],
  );
  const [currentStatuses, setCurrentStatuses] = useState<string[]>([]);

  // Get leads data with current parameters (only motorcycles)
  const leadsQuery = useGetAllLeads(
    currentPage,
    itemsPerPage,
    currentStatuses.length ? currentStatuses : undefined,
    "motorcycle",
    searchQuery || undefined
  );
  const leads = leadsQuery.data?.data || [];
  const totalLeads = leadsQuery.data?.total || 0;
  const isLoadingLeads = leadsQuery.isLoading;
  const leadsError = leadsQuery.error;

  // Dialog state for period selection
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(new Date());

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleLeadClick = (id: string) => {
    navigate(`/admin/leads/${id}`);
  };

  const handleStatusesChange = (statuses: string[]) => {
    setCurrentStatuses(statuses);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // The query will automatically update when state changes
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    // The query will automatically update when state changes
  };

  const handlePeriodConfirm = () => {
    setDialogOpen(false);
    toast(leadUILabels(getLanguage()).periodUpdatedSuccess, {
      duration: 3000,
      dismissible: true,
    });
  };

  return (
    <LeadboxLayout>
      <div className="max-w-[1000px] mx-auto">
        <div className="flex flex-col justify-center mb-6">
          <div className="flex gap-4 items-center justify-between">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight">
              {commonTitleLabels(getLanguage()).motorcycleLeadbox}
            </h1>
            <Button
              onClick={() => leadsQuery.refetch()}
              variant="outline"
              className="backdrop-blur"
              disabled={isLoadingLeads}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${
                  isLoadingLeads ? "animate-spin" : ""
                }`}
              />
              {commonButtonLabels(getLanguage()).update}
            </Button>
            {/* TODO: Kika m Osman om det ska finnas 2 sökrutor i denna sida? Tar bort denna temporärt? */}
            {/* <div className="flex-shrink-0">
                <LeadboxHeader onSearch={handleSearch} />
              </div> */}
          </div>
        </div>

        <main className="md:space-y-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            {/* TODO: Bortkommenterat för nu <LeadStats 
                  data={mockStatsData} 
                  totalLeads={30} 
                  onPeriodClick={() => setDialogOpen(true)} 
                  dateRange={`${format(startDate, "yyyy-MM-dd")} till ${format(endDate, "yyyy-MM-dd")}`}
                  type="motorcycle"
                /> */}
            <DialogContent className="w-[calc(100%-32px)] max-h-[90vh] overflow-y-auto mx-4 md:w-[600px] md:mx-auto">
              <DialogHeader>
                <DialogTitle>
                  {leadUILabels(getLanguage()).selectPeriod}
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 md:grid-cols-2 mt-4">
                <div className="space-y-2">
                  <Label>{leadUILabels(getLanguage()).fromDate}</Label>
                  <DatePicker
                    value={startDate}
                    onChange={(date) => date && setStartDate(date)}
                    maxDate={endDate}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{leadUILabels(getLanguage()).toDate}</Label>
                  <DatePicker
                    value={endDate}
                    onChange={(date) => date && setEndDate(date)}
                    minDate={startDate}
                    maxDate={new Date()}
                  />
                </div>
              </div>
              <div className="mt-6">
                <Button onClick={handlePeriodConfirm} className="w-full">
                  <Check className="mr-2" />
                  {leadUILabels(getLanguage()).confirmPeriod}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="pt-6">
            {isLoadingLeads ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-blue-500 border-solid"></div>
                <span className="ml-2 text-muted-foreground">
                  {commonMiscLabels(getLanguage()).loadingData}
                </span>
              </div>
            ) : leadsError ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-center">
                  <p className="text-red-600 mb-2">
                    {leadUILabels(getLanguage()).couldNotLoadLeads}
                  </p>
                  <Button
                    onClick={() => leadsQuery.refetch()}
                    variant="outline"
                  >
                    {commonMiscLabels(getLanguage()).tryAgain}
                  </Button>
                </div>
              </div>
            ) : (
              <Card className="glass-card p-6">
                <LeadTableNew
                  leads={leads}
                  onLeadClick={handleLeadClick}
                  vehicleType="motorcycle"
                  onSearch={handleSearch}
                  isSearching={isLoadingLeads}
                  totalLeads={totalLeads}
                  onStatusesChange={handleStatusesChange}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  selectedStatuses={currentStatuses}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                />
              </Card>
            )}
          </div>
        </main>
      </div>
    </LeadboxLayout>
  );
};

export default MCLeads;
